import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { IMediaFile } from '../../../core/uploader/media-file.interface';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslationHandleService } from '../../../core/translate/translation-handle.service';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IdentityService } from '../../../services/identity.service';
import { identifierMaxLength, slugPattern } from '../../../core/utils/validators';
import { CoordinatesValidatorsRegex } from '../../../shared/coordinates-validators-regex.enum';
import { TeamQrCodeAccessType } from '../../components/team-qr-codes/models/team-qr-code-access-type';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { AddProjectData } from './add-project-data.interface';
import { UrlModifyService } from '../../../core/url-modify.service';
import { TeamReportCategory } from '../../teams/models/team-report-category';
import { TeamInterface } from '../../teams/models/team.interface';
import { ApplicationOptions, ICompany } from '../../companies/shared/interfaces/company.interface';
import { Permission } from '../../../shared/interfaces/permission';
import { PERMISSIONS_DEFAULT } from '../../../shared/models/permissions-default.model';
import { CompanyCoordinates } from '../../teams/models/company-coordinates';
import { TeamsOptions } from '../../teams/models/teams-options';
import { ILayer } from '../../components/layers/shared/models/layer.interface';
import { LayersService } from '../../components/layers/shared/services/layers.service';
import { Layer } from '../../teams/models/layer';
import { TeamSceneLayer } from '../../teams/models/team-scene-layer';
import { CommonSceneLayer } from '../../teams/models/common-scene-layer';
import { TeamLayersAdapterService } from '../../teams/services/team-layers.adapter.service';
import { sortBy } from 'lodash';
import { AutofillFieldService } from '../../../services/autofill-field.service';
import { LoadingService } from '../../../core/loading-overlay/loading.service';
import { snackBarConfig } from '../../../core/core.module';
import { MediaService } from '../../../core/uploader/media.service';
import { TeamsService } from '../../teams/services/teams.service';
import { EMPTY, Observable, of } from 'rxjs';
import { MatLegacySnackBar as MatSnackbar } from '@angular/material/legacy-snack-bar';
import { TeamAdapterService } from '../../teams/services/team-adapter.service';
import { TeamProjectService } from '../../../services/team-project.service';
import { Identity } from '../../../interfaces/identity';
import { HttpErrorResponse } from '@angular/common/http';

const PHRASES_FOR_TRANSLATE = [
  {
    updateImage: 'system.updateImage',
  },
  {
    createTeamSuccessMsg: 'teams.form.creating.success_msg',
  },
  {
    serverError: 'system.serverError',
  },
  {
    errorOccurred: 'errors.error_occurred',
  },
];

@Component({
  selector: 'mee-add-project',
  templateUrl: './add-project.component.html',
  styleUrls: ['./add-project.component.scss'],
  providers: [TeamLayersAdapterService, MediaService, TeamAdapterService],
})
export class AddProjectComponent implements OnInit {
  form: FormGroup;
  isLogoTouched = false;
  translationMap: Map<string, string>;
  team: TeamInterface;
  permissions = PERMISSIONS_DEFAULT;
  coordinates: CompanyCoordinates;
  options: TeamsOptions[];
  routedApplicationOptions: ApplicationOptions;
  companySceneLayers: Layer[] = [];
  commonSceneLayers: CommonSceneLayer[] = [];
  protected readonly identifierMaxLength = identifierMaxLength;
  private readonly translationHandleService = inject(TranslationHandleService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly identityService = inject(IdentityService);
  private readonly matDialogRef = inject(MatDialogRef);
  private readonly matDialogData: AddProjectData = inject(MAT_DIALOG_DATA);
  private readonly urlModifyService = inject(UrlModifyService);
  private readonly layersService = inject(LayersService);
  private readonly teamLayersAdapterService = inject(TeamLayersAdapterService);
  private readonly autoFillFieldsService = inject(AutofillFieldService);
  private readonly mediaService = inject(MediaService);
  private readonly teamsService = inject(TeamsService);
  private readonly snackBar = inject(MatSnackbar);
  private readonly teamAdapterService = inject(TeamAdapterService);
  private readonly teamProjectService = inject(TeamProjectService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  readonly loadingService = inject(LoadingService);
  selectedCompanySlug = this.identityService.companySlug;
  company = this.matDialogData.company;

  isTeamAdmin(): boolean {
    return this.identityService.isTeamAdmin();
  }

  isProjectCreator(): boolean {
    return this.identityService.isProjectCreator();
  }

  isSuperAdmin(): boolean {
    return this.identityService.isSuperAdmin();
  }

  ngOnInit(): void {
    this.createForm();
    this.translationHandleService.setPhrasesForTranslate(PHRASES_FOR_TRANSLATE);
    this.getTranslationMap();
    this.initializeProjectFields();
  }

  logoUploaded(file: IMediaFile): void {
    this.form.controls.LogoUrl.setValue(file.sourceFile.FileUrl);
    this.isLogoTouched = true;
  }

  isRequiredError(formControlName: string): boolean {
    return (
      this.form.get(formControlName).errors &&
      this.form.get(formControlName).errors.required &&
      (this.form.get(formControlName).dirty || this.form.get(formControlName).touched)
    );
  }

  isNotValidError(formControlName: string): boolean {
    return this.form.get(formControlName).errors && this.form.get(formControlName).errors.pattern;
  }

  cancel() {
    this.matDialogRef.close();
  }

  create() {
    this.loadingService.run();

    const companySlug = this.form.get('CompanySlug').value;

    if (!this.isLogoTouched) {
      let companyLogoUrl = this.form.get('LogoUrl').value;

      companyLogoUrl = companyLogoUrl.replace('?', '&');

      const companyLogoUriParam = new URLSearchParams(companyLogoUrl).get('uri');

      let companyLogoUriParamWithoutSlash = companyLogoUriParam;

      if (companyLogoUriParam[0] === '/') {
        companyLogoUriParamWithoutSlash = companyLogoUriParam.slice(1, companyLogoUriParam.length);
      }

      this.mediaService
        .copy('TeamsLogo', companyLogoUriParamWithoutSlash)
        .pipe(
          switchMap(teamLogo => {
            this.form.get('LogoUrl').setValue(teamLogo);

            return this.teamsService.create(companySlug || this.identityService.companySlug, this.getTeamDataForRequest()).pipe(
              switchMap(team =>
                this.isProjectCreator()
                  ? this.identityService.refreshAccessToken().pipe(
                      tap((identity: Identity) => (this.identityService.identity = identity)),
                      switchMap(() => of(team)),
                    )
                  : of(team),
              ),
              catchError((error: HttpErrorResponse) => {
                if (error.status === 403) {
                  return this.handle403Error();
                }

                return EMPTY;
              }),
            );
          }),
          tap(() => {
            if (this.isTeamAdmin()) {
              this.teamProjectService.updateList();
            }
            this.matDialogRef.close();
            this.snackBar.open(this.translationMap.get('createTeamSuccessMsg'), 'OK', snackBarConfig);
          }),
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.loadingService.stop()),
        )
        .subscribe();

      return;
    }

    this.teamsService
      .create(companySlug || this.identityService.companySlug, this.getTeamDataForRequest())
      .pipe(
        switchMap(team =>
          this.isProjectCreator()
            ? this.identityService.refreshAccessToken().pipe(
                tap((identity: Identity) => (this.identityService.identity = identity)),
                switchMap(() => of(team)),
              )
            : of(team),
        ),
        tap(() => {
          if (this.isTeamAdmin()) {
            this.teamProjectService.updateList();
          }
          this.matDialogRef.close();
          this.snackBar.open(this.translationMap.get('createTeamSuccessMsg'), 'OK', snackBarConfig);
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 403) {
            return this.handle403Error();
          }

          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loadingService.stop()),
      )
      .subscribe();
  }

  handle403Error(): Observable<Identity> {
    return this.identityService.refreshAccessToken().pipe(
      tap((identity: Identity) => {
        this.identityService.identity = identity;
        this.matDialogRef.close();
        this.changeDetectorRef.markForCheck();
      }),
      switchMap(() => EMPTY),
    );
  }

  private initializeProjectFields(): void {
    this.form.get('LogoUrl').setValue(this.urlModifyService.modifyUrl(this.company.LogoUrl, 192, 192));
    this.setTeamReportCategories(this.matDialogData.reportCategories);
    this.team = {
      SceneLayers: [],
      IsNewDtmEnabledByDefault: false,
      CustomCrsReferences: [],
      DataSources: [],
      Dtms: [],
      Maps: [],
      ShouldAddNewSceneLayers: false,
    } as TeamInterface;
    this.form.get('CompanySlug').setValue(this.selectedCompanySlug);
    this.setInheritedPermissions(this.company);
    this.setDefaultIsNewEnabledByDefault();
    this.coordinates = new CompanyCoordinates(this.company.Slug, this.company.DefaultLatitude, this.company.DefaultLongitude);
    this.options = [{ slug: this.company.Slug, options: this.company.ApplicationOptions }];
    this.routedApplicationOptions = this.options[0].options;
    this.getLayers();
    this.setCoordinatesForCompanyAdmin();
    this.setAutoFillValue();
  }

  private createForm() {
    this.form = this.formBuilder.group({
      CompanySlug: [''],
      Slug: ['', [Validators.required, Validators.pattern(slugPattern)]],
      Name: [''],
      IsBranded: true,
      CategoryName: [''],
      LogoUrl: [''],
      DefaultLatitude: ['', [Validators.required, Validators.pattern(CoordinatesValidatorsRegex.Latitude)]],
      DefaultLongitude: ['', [Validators.required, Validators.pattern(CoordinatesValidatorsRegex.Longitude)]],
      Disclaimer: [''],
      DataCollectionMinAccuracy: [null],
      Permissions: [[]],
      IssueReportCategories: [],
      ExternalAccessToken: [],
      VGisScanAccessToken: [],
      QrCodeAccessType: [TeamQrCodeAccessType.Public],
      QrCodePin: [null],
    });
  }

  private setInheritedPermissions({ Permissions }: ICompany) {
    this.team = { ...this.team, Permissions };
    this.onPermissionChange(Permissions);
  }

  private setDefaultIsNewEnabledByDefault(): void {
    this.team = { ...this.team, IsNewDtmEnabledByDefault: false };
  }

  private onPermissionChange(permissions: Permission[]) {
    this.form.markAsDirty();
    this.form.get('Permissions').setValue(permissions);
    this.permissions = permissions;
  }

  private getLayers(): void {
    this.layersService
      .getLayersByCompany(this.identityService.companySlug)
      .pipe(
        tap((layers: ILayer[]) => {
          this.companySceneLayers = layers;
          this.setCommonSceneLayers();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private getTranslationMap() {
    this.translationHandleService
      .getTranslationMap()
      .pipe(
        tap(translationMap => (this.translationMap = translationMap)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private setCommonSceneLayers() {
    const teamLayersWithOrder = this.team.SceneLayers.map((sceneLayer: TeamSceneLayer, index) => ({ ...sceneLayer, order: index }));

    this.commonSceneLayers = this.teamLayersAdapterService.getCommonSceneLayers(this.companySceneLayers, teamLayersWithOrder);

    this.commonSceneLayers = sortBy(this.commonSceneLayers, ['order']);
  }

  private setCoordinatesForCompanyAdmin() {
    this.form.get('DefaultLatitude').setValue(this.coordinates.DefaultLatitude);
    this.form.get('DefaultLongitude').setValue(this.coordinates.DefaultLongitude);
  }

  private setAutoFillValue(): void {
    const nameControl = this.form.get('Name');
    const identifierControl = this.form.get('Slug');

    this.autoFillFieldsService.setAutoFillValue$(nameControl, identifierControl).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private getTeamDataForRequest() {
    const applicationOptions = this.routedApplicationOptions;

    return this.teamAdapterService.getTeamDataForRequest(
      this.team,
      this.form.value,
      this.permissions,
      this.commonSceneLayers,
      applicationOptions,
    );
  }

  private setTeamReportCategories(categories: TeamReportCategory[]): void {
    const reportCategories = categories.map(category => {
      const Id = crypto.randomUUID();

      return { Id, ...category };
    });

    this.form.get('IssueReportCategories').setValue(reportCategories);
  }
}
